'use client';

import React, {
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  memo,
  type ReactNode,
  useRef,
} from 'react';
import { mergeProps } from 'react-aria';
import { SearchField } from 'components/SearchField';
import type {
  ClearButtonProps,
  NavigateBackProps,
  SearchFieldProps,
} from 'components/SearchField/SearchField';
import { Separator } from 'components/Separator';
import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect';
import { mergeRefs } from '@react-aria/utils';
import { theme, type ThemeSpaces } from '@frameio/vapor-theme';
import styles from '../../Collections.module.css';
import type { ListFilterState } from '../types';
import { useFilteredListSearch } from '../useFilteredListSearch';
import { useListStateContext } from './ListStateContext';

type ListSearchProps<T> = {
  /** The state of the list filter */
  state?: ListFilterState<T>;
  /** The gap between the divider and input */
  gapY?: ThemeSpaces | string;
  /** Optional separator component */
  separator?: ReactNode;
} & Pick<
  SearchFieldProps,
  | 'adornmentBefore'
  | 'autoFocus'
  | 'autoFocusWithoutScrolling'
  | 'debounce'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'onQueryClear'
  | 'pendingStateLabel'
  | 'placeholder'
  | 'size'
  | 'variant'
  | 'iconVariant'
> &
  ClearButtonProps &
  NavigateBackProps &
  PaddingProps;

/**
 * Props for controlling padding around the ListSearch component.
 * Supports both traditional padding props and modern CSS logical properties.
 * @example
 * ```tsx
 * <ListSearch paddingX={12} paddingY={8} />
 * <ListSearch paddingInline={12} paddingBlock={8} />
 * ```
 */
type PaddingProps = {
  /**
   * The padding-block-start css property
   */
  paddingBlockStart?: ThemeSpaces;
  /**
   * The padding-block-end css property
   */
  paddingBlockEnd?: ThemeSpaces;
  /**
   * The padding-inline-start css property
   */
  paddingInlineStart?: ThemeSpaces;
  /**
   * The padding-inline-end css property
   */
  paddingInlineEnd?: ThemeSpaces;
  /**
   * The padding-inline css property
   */
  paddingX?: ThemeSpaces;
  /**
   * The padding-block css property
   */
  paddingY?: ThemeSpaces;
  /**
   * @deprecated  use paddingBlockStart instead
   */
  paddingTop?: ThemeSpaces;
  /**
   * @deprecated use paddingInlineEnd instead
   */
  paddingRight?: ThemeSpaces;
  /**
   * @deprecated use paddingBlockEnd instead
   */
  paddingBottom?: ThemeSpaces;
  /**
   * @deprecated  use paddingInlineStart instead
   */
  paddingLeft?: ThemeSpaces;
};

export const ListSearch = memo(
  forwardRef(function ListSearchComponent<T extends object>(
    {
      state: stateProp,
      separator = <Separator color="secondary" />,

      // group deprecated props for aliasing and defaulting
      paddingTop,
      paddingRight,
      paddingBottom,
      paddingLeft,

      paddingBlockEnd,
      paddingBlockStart = paddingTop ?? 12, // use behavior for backwards compatibility
      paddingInlineEnd,
      paddingInlineStart,
      paddingX = 12,
      paddingY,
      gapY = 12,

      debounce = 0,

      ...otherProps
    }: ListSearchProps<T>,
    forwardedRef?: ForwardedRef<HTMLInputElement>
  ) {
    const stateContext = useListStateContext();
    const state = stateProp ?? stateContext;
    const { inputProps, selectionCache } = useFilteredListSearch(state);
    const inputElementRef = useRef<HTMLInputElement | null>(null);
    /**
     * the order of the refs is important, the forwardedRef should be first so that it can be used to set the ref on the input element
     */
    const combinedRef = mergeRefs(forwardedRef || null, inputElementRef);

    /**
     * Note: the order of the props is important, the onChange prop should be first so that it can be used to set the selection range on the input element
     */
    const mergedSearchFieldProps = mergeProps(
      inputProps,
      otherProps
    ) as ListSearchProps<T>;

    useIsomorphicLayoutEffect(() => {
      /**
       * Restore cursor position if the input value matches the cached value and the component's controlled value also matches.
       * This prevents cursor jumps during unrelated re-renders or if the value was transformed externally.
       */
      const inputElement = inputElementRef.current;
      if (
        inputElement?.value === selectionCache.current.value &&
        selectionCache.current.value === state.queryState.query &&
        document.activeElement === inputElement
      ) {
        inputElement.setSelectionRange(
          selectionCache.current.start,
          selectionCache.current.end
        );
      }
    }, [state.queryState.query, selectionCache]);

    return (
      <div
        className={styles.listSearchContainer}
        style={
          {
            '--vapor-list-search-gap-y': formatValue(gapY),
            '--vapor-list-search-padding-block-end': formatValue(
              paddingBlockEnd ?? paddingBottom ?? paddingY
            ),
            '--vapor-list-search-padding-block-start': formatValue(
              paddingBlockStart ?? paddingTop ?? paddingY
            ),
            '--vapor-list-search-padding-inline-end': formatValue(
              paddingInlineEnd ?? paddingRight ?? paddingX
            ),
            '--vapor-list-search-padding-inline-start': formatValue(
              paddingInlineStart ?? paddingLeft ?? paddingX
            ),
          } as CSSProperties
        }
      >
        <SearchField
          ref={combinedRef}
          debounce={debounce}
          enableSpinner={state.isAsync}
          isSearching={state.isLoadingVisible}
          variant="pill"
          {...mergedSearchFieldProps}
        />
        {separator || null}
      </div>
    );
  })
);

ListSearch.displayName = 'ListSearch';

function formatValue(
  value: ThemeSpaces | string | undefined | null
): string | undefined | null {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') return value;
  return theme.spaces[value];
}
